/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { FilePlus2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '../../api';
import type { DocumentStage, RequiredDocument, RequirementSet } from '../../api/areas/namechange';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Field, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ActionDialog, FormFooter, ReasonField, selectClass, type DialogBodyProps } from '../operator/shared';
import { alternativesOf } from '../../lib/requirementNames';
import { CheckField, instant, NumberField, useDocName, useEnum, useLocalName } from './shared';

const blank = (): RequiredDocument => ({ code: '', name_vi: '', name_en: '', required: true, one_of: null, template_ref: null, signed_by: null, stage: 'BEFORE_APPROVAL', notes_vi: null, notes_en: null });
const orNull = (s: string) => (s.trim() ? s.trim() : null);

/** A new version of one set, prefilled with the latest version. */
function NewVersionForm({ close, changed, base }: DialogBodyProps & { base: RequirementSet }) {
  const { t } = useTranslation('services');
  const e = useEnum();
  const [titleVi, setTitleVi] = useState(base.title_vi);
  const [titleEn, setTitleEn] = useState(base.title_en);
  const [legal, setLegal] = useState(base.legal_basis);
  const [reviewDays, setReviewDays] = useState(base.review_days);
  const [docs, setDocs] = useState<RequiredDocument[]>(base.documents.map((d) => ({ ...d })));
  const [from, setFrom] = useState('');
  const [reason, setReason] = useState('');
  const set = (i: number, patch: Partial<RequiredDocument>) => setDocs((ds) => ds.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const w = useWrite((_: null, opts) =>
    api.publishRequirementSet(
      { case_code: base.case_code, sub_case: base.sub_case, effective_from: instant(from), title_vi: titleVi.trim(), title_en: titleEn.trim(), legal_basis: legal.trim(), review_days: reviewDays, documents: docs, reason: reason.trim() },
      opts,
    ),
  );
  const done = w.state.phase === 'done' ? w.state.result : null;
  const valid = docs.length > 0 && docs.every((d) => /^[A-Z][A-Z0-9_]{1,63}$/.test(d.code) && d.name_vi.trim() && d.name_en.trim()) && titleVi.trim() && titleEn.trim() && legal.trim() && reason.trim();
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok">{t('requirements.published', { version: done.version })}</Callout>
      ) : (
        <>
          <Callout tone="info">{t('requirements.versionHint')}</Callout>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('requirements.titleVi')}>
              <Input value={titleVi} onChange={(ev) => setTitleVi(ev.target.value)} maxLength={300} />
            </Field>
            <Field label={t('requirements.titleEn')}>
              <Input value={titleEn} onChange={(ev) => setTitleEn(ev.target.value)} maxLength={300} />
            </Field>
          </div>
          <Field label={t('requirements.legalBasis')}>
            <Textarea value={legal} onChange={(ev) => setLegal(ev.target.value)} rows={2} maxLength={2000} />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <NumberField label={t('requirements.reviewDays')} value={reviewDays} onChange={setReviewDays} min={1} max={90} />
            <Field label={t('common.effectiveFrom')} hint={t('common.effectiveFromHint')}>
              <Input type="datetime-local" value={from} onChange={(ev) => setFrom(ev.target.value)} />
            </Field>
          </div>
          <div className="grid gap-3">
            <p className="text-sm font-medium">{t('requirements.documents')}</p>
            {docs.map((d, i) => (
              <div key={i} className="grid gap-2 rounded-md border p-3">
                <div className="grid gap-2 sm:grid-cols-3">
                  <Field label={t('requirements.code')}>
                    <Input value={d.code} onChange={(ev) => set(i, { code: ev.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })} className="font-mono text-xs" />
                  </Field>
                  <Field label={t('requirements.stage')}>
                    <select className={selectClass} value={d.stage} onChange={(ev) => set(i, { stage: ev.target.value as DocumentStage })}>
                      {(['BEFORE_APPROVAL', 'AFTER_APPROVAL'] as const).map((s) => (
                        <option key={s} value={s}>
                          {e('ncStage', s)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t('requirements.oneOf')} hint={t('requirements.oneOfHint')}>
                    <Input value={d.one_of ?? ''} onChange={(ev) => set(i, { one_of: orNull(ev.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '')) })} className="font-mono text-xs" />
                  </Field>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Field label={t('requirements.nameVi')}>
                    <Input value={d.name_vi} onChange={(ev) => set(i, { name_vi: ev.target.value })} />
                  </Field>
                  <Field label={t('requirements.nameEn')}>
                    <Input value={d.name_en} onChange={(ev) => set(i, { name_en: ev.target.value })} />
                  </Field>
                  <Field label={t('requirements.template')}>
                    <Input value={d.template_ref ?? ''} onChange={(ev) => set(i, { template_ref: orNull(ev.target.value) })} placeholder="Mẫu số 24 — NĐ 115/2025" />
                  </Field>
                  <Field label={t('requirements.signedBy')}>
                    <select className={selectClass} value={d.signed_by ?? ''} onChange={(ev) => set(i, { signed_by: orNull(ev.target.value) })}>
                      <option value="">—</option>
                      {['BOTH_PARTIES', 'TRANSFEROR', 'TRANSFEREE', 'HOLDER', 'NEW_HOLDER', 'AUTHORITY'].map((s) => (
                        <option key={s} value={s}>
                          {e('signedBy', s)}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex flex-wrap items-center gap-4">
                    <CheckField label={t('requirements.required')} checked={d.required} onChange={(v) => set(i, { required: v })} />
                    <CheckField
                      label={t('requirements.perDomain')}
                      checked={d.source === 'DOMAIN_DECLARATION'}
                      onChange={(v) => set(i, v ? { source: 'DOMAIN_DECLARATION', stage: 'BEFORE_APPROVAL', one_of: null } : { source: null })}
                    />
                  </span>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setDocs((ds) => ds.filter((_, j) => j !== i))}>
                    <Trash2 /> {t('requirements.remove')}
                  </Button>
                </div>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" onClick={() => setDocs((ds) => [...ds, blank()])}>
              <Plus /> {t('requirements.addDocument')}
            </Button>
          </div>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!valid} submit={done ? undefined : t('requirements.publish')} onClose={close} />
    </form>
  );
}

/** `/settings/name-change-requirements`: the documents of each case, versioned. */
export function RequirementSetsPage() {
  const { t } = useTranslation('services');
  const e = useEnum();
  const name = useLocalName();
  const docName = useDocName();
  useDocumentTitle(t('requirements.title'));
  const result = useApi((signal) => api.listRequirementSets({ signal }), []);
  if (result.status === 'loading') return <Loading rows={4} />;
  if (result.status === 'error') return <DataError error={result.error} title={t('common.loadFailed')} onRetry={result.reload} />;
  const { items, in_force } = result.data;
  const groups = [...new Map(items.map((s) => [`${s.case_code}/${s.sub_case ?? ''}`, s])).values()];
  const docCols = (docs: RequiredDocument[]): Column<RequiredDocument>[] => [
    {
      key: 'doc',
      header: t('nameChange.col.document'),
      cell: (d) => (
        <span className="grid gap-0.5">
          <span>{docName(d)}</span>
          <span className="font-mono text-xs text-muted-foreground">{d.code}</span>
        </span>
      ),
    },
    { key: 'stage', header: t('nameChange.col.stage'), cell: (d) => e('ncStage', d.stage) },
    { key: 'req', header: t('nameChange.col.required'), cell: (d) => (d.required ? (d.one_of ? <span title={d.one_of}>{t('nameChange.oneOf', { others: alternativesOf(d, docs, docName) })}</span> : t('common.yes')) : t('common.no')) },
    {
      key: 'tpl',
      header: t('nameChange.col.template'),
      cell: (d) => (
        <span className="grid gap-0.5 text-xs">
          <span>{d.template_ref ?? '—'}</span>
          {d.source === 'DOMAIN_DECLARATION' ? <span className="text-muted-foreground">{t('nameChange.generatedPerDomain')}</span> : null}
        </span>
      ),
    },
    { key: 'sign', header: t('nameChange.col.signedBy'), cell: (d) => <span className="text-xs">{d.signed_by ? e('signedBy', d.signed_by) : '—'}</span> },
  ];
  return (
    <>
      <PageHeader
        title={t('requirements.title')}
        subtitle={t('requirements.subtitle')}
        crumbs={[{ label: t('crumbs.settings') }]}
        actions={
          <Button type="button" variant="outline" size="sm" onClick={result.reload}>
            <RefreshCw /> {t('common.refresh')}
          </Button>
        }
      />
      <div className="flex flex-col gap-6">
        {groups.map((latest) => {
          const versions = items.filter((s) => s.case_code === latest.case_code && s.sub_case === latest.sub_case).sort((a, b) => b.version - a.version);
          const current = versions.find((v) => in_force.includes(v.id)) ?? versions[versions.length - 1] ?? latest;
          const newest = versions[0] ?? latest;
          return (
            <Section
              key={`${latest.case_code}/${latest.sub_case ?? ''}`}
              title={`${e('ncCase', latest.case_code)}${latest.sub_case ? ` · ${e('ncSubCase', latest.sub_case)}` : ''}`}
              description={`${name({ name_vi: current.title_vi, name_en: current.title_en })} — ${current.legal_basis}`}
              flush
              actions={
                <ActionDialog label={t('requirements.newVersion')} icon={FilePlus2} title={t('requirements.newVersionTitle')} wide onChanged={result.reload}>
                  {(p) => <NewVersionForm {...p} base={newest} />}
                </ActionDialog>
              }
            >
              <div className="flex flex-wrap items-center gap-2 px-5 pb-3 text-xs">
                <Badge tone="ok">{t('requirements.inForce', { version: current.version })}</Badge>
                <span>{t('requirements.reviewDaysValue', { count: current.review_days })}</span>
                {versions
                  .filter((v) => v.id !== current.id)
                  .map((v) => (
                    <Badge key={v.id} tone={Date.parse(v.effective_from) > Date.now() ? 'pending' : 'neutral'} title={v.reason}>
                      v{v.version} · {formatDateTime(v.effective_from)}
                    </Badge>
                  ))}
              </div>
              <DataTable columns={docCols(current.documents)} rows={current.documents} rowKey={(d) => d.code} empty={<EmptyState title={t('nameChange.empty')} />} />
            </Section>
          );
        })}
      </div>
    </>
  );
}
